import ColorSelector from '@components/ColorSelector/ColorSelector';
import { ENABLE_SAVE_CONTACT_CUSTOM_COLOR } from '@components/EmailSignaturev2/consts';
import { SideEditorNodeKey } from '@components/EmailSignaturev2/types';
import { FormSelector, FormSelectorGroup } from '@components/FormSelector/FormSelector';
import { SideEditorScrollType } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import Toggle from '@components/Toggle/Toggle';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
import useNode from '@hooks/use-node';
import Emitter from '@modules/event-emitter';
import { type FC, useContext, useEffect } from 'react';

import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorSaveContactSection.module.scss';

const EmailSignatureEditorSaveContactSection: FC<React.PropsWithChildren<unknown>> = () => {
  const { changeTab, registerNode, panelIndex, scrollIntoView } = useSideEditorContext();
  const { isInTransitionState, emailSignature, emailSignatureDispatch, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const [saveContactDesignSectionDiv, saveContactDesignSectionDivRef] = useNode<HTMLDivElement>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToSaveContact = () => {
      if (isInTransitionState) {
        return;
      }

      scrollIntoView(SideEditorNodeKey.SAVE_CONTACT_DESIGN_NODE, SideEditorScrollType.START, false);
    };
    Emitter.on('SCROLL_TO_SAVE_CONTACT', handleScrollToSaveContact);

    return () => {
      Emitter.off('SCROLL_TO_SAVE_CONTACT', handleScrollToSaveContact);
    };
  }, [changeTab, isInTransitionState, scrollIntoView]);

  useEffect(() => {
    if (!saveContactDesignSectionDiv) return;
    registerNode(SideEditorNodeKey.SAVE_CONTACT_DESIGN_NODE, {
      panelBelongTo: panelIndex,
      node: saveContactDesignSectionDiv,
    });
  }, [saveContactDesignSectionDiv, registerNode, panelIndex]);

  return (
    <EmailSignatureEditorPanelSection ref={saveContactDesignSectionDivRef}>
      <EmailSignatureEditorPanelSectionHeading heading="Save contact" />
      <EmailSignatureEditorPanelSectionContents>
        <div className={styles['save-card-field-option']} data-direction="horizontal">
          <div className={styles['save-card-field-label']}>Display button</div>
          <Toggle
            disabled={isSignatureLocked}
            state={emailSignature.saveContact.displaySaveContact}
            onChange={(isChecked) => {
              emailSignatureDispatch({
                type: 'UPDATE_SAVE_CONTACT_VISIBLE',
                value: isChecked,
              });
            }}
          />
        </div>
        {emailSignature.saveContact.displaySaveContact && ENABLE_SAVE_CONTACT_CUSTOM_COLOR && (
          <div className={styles['save-card-field-option']} data-direction="vertical">
            <div className={styles['save-card-field-label']}>Button color</div>
            <div className={styles['save-card-field-multi-steps']}>
              <FormSelectorGroup
                className={styles['save-card-group-form-selector']}
                selectedValue={emailSignature.saveContact.buttonColorUseThemeColor.toString()}
                onChange={(newValue) => {
                  emailSignatureDispatch({
                    type: 'UPDATE_SAVE_BUTTON_COLOR_USE_THEME',
                    value: newValue === 'true',
                  });
                }}
                size="sm"
              >
                <div className={styles['save-card-form-selector']}>
                  <FormSelector disabled={isSignatureLocked} value={'true'}>
                    Theme Color
                  </FormSelector>
                </div>
                <div className={styles['save-card-form-selector']}>
                  <FormSelector disabled={isSignatureLocked} value={'false'}>
                    Custom
                  </FormSelector>
                </div>
              </FormSelectorGroup>
              {!emailSignature.saveContact.buttonColorUseThemeColor && (
                <ColorSelector
                  disabled={isSignatureLocked}
                  color={emailSignature.saveContact.buttonColor}
                  onSelect={(color) => {
                    emailSignatureDispatch({
                      type: 'UPDATE_SAVE_BUTTON_COLOR',
                      value: color,
                    });
                  }}
                />
              )}
            </div>
          </div>
        )}
        {isSignatureLocked && (
          <EmailSignatureLockLayer message="You cannot edit this styling, please contact your admin if you have any questions" />
        )}
      </EmailSignatureEditorPanelSectionContents>
    </EmailSignatureEditorPanelSection>
  );
};

export default EmailSignatureEditorSaveContactSection;
